import { useMemo, useState, type JSX } from 'react';
import { Link, useSearch } from '@tanstack/react-router';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  type Node,
  type Edge,
  type NodeProps,
  Handle,
  Position,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { shaktiApi, ApiError } from '../api/client';

import { useProjectScopedEffect } from '../stores/project';
/**
 * Lifecycle Graph — FEAT-ADD-030 (closes B4 from the 2026-04-17 release audit).
 *
 * Replaces the previous CSS-only list rendering with a real interactive
 * DAG powered by `@xyflow/react`. Users can pan / zoom / drill-down into
 * individual phase nodes; status is colour-coded and updated live from
 * `shaktiApi.getPipelineDag`.
 *
 * Layered layout is computed inline (topological breadth). xyflow's
 * automatic node-positioning requires either a layout algorithm or
 * pre-computed positions; we use the latter because the 12-phase SDLC
 * pipeline has a deterministic shape the server tells us about.
 *
 * Accessibility: respects `prefers-reduced-motion` by disabling
 * auto-pan on node-click; keyboard-navigable via xyflow's built-in
 * focus management.
 */
export function LifecycleRoute(): JSX.Element {
  const search = useSearch({ strict: false }) as { pipeline_id?: string };
  const pipelineId = search.pipeline_id ?? 'default';

  const [dag, setDag] = useState<{
    nodes: Array<{ id: string; label: string; status: string }>;
    edges: Array<{ source: string; target: string }>;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  // N5.5 / W1.1 (v1.1) — project-scoped; aborts in-flight on switch.
  useProjectScopedEffect(
    (signal) => {
      shaktiApi
        .getPipelineDag(pipelineId, { signal })
        .then((d) => {
          if (!signal.aborted) setDag(d);
        })
        .catch((e: unknown) => {
          if (signal.aborted) return;
          setError(e instanceof ApiError ? e.body?.error ?? e.message : 'Failed to load DAG');
        });
    },
    [pipelineId],
  );

  const { nodes: flowNodes, edges: flowEdges } = useMemo(
    () => toReactFlow(dag),
    [dag],
  );

  const selectedNode = selected && dag ? dag.nodes.find((n) => n.id === selected) : null;
  const incoming = selected && dag ? dag.edges.filter((e) => e.target === selected) : [];
  const outgoing = selected && dag ? dag.edges.filter((e) => e.source === selected) : [];

  return (
    <section aria-labelledby="lc-title" className="mx-auto flex max-w-6xl flex-col gap-6">
      <header>
        <span className="shakti-mono text-2xs uppercase tracking-wider text-fg-muted">
          /dashboard/lifecycle
        </span>
        <h1 id="lc-title" className="text-2xl font-semibold text-fg-primary">
          Lifecycle Graph
        </h1>
        <p className="text-sm text-fg-secondary">
          Phase DAG for pipeline <code className="shakti-mono">{pipelineId}</code>.
        </p>
      </header>

      {error ? (
        <div role="alert" className="shakti-card border border-status-error/40 bg-status-error/10 text-status-error">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_300px]">
        <div
          className="shakti-card p-0 overflow-hidden"
          style={{ height: 560 }}
          aria-label="Phase DAG visualization"
        >
          {dag ? (
            <ReactFlow
              nodes={flowNodes}
              edges={flowEdges}
              nodeTypes={{ phase: PhaseNode }}
              onNodeClick={(_, node) => setSelected(node.id)}
              fitView
              proOptions={{ hideAttribution: true }}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable
              panOnScroll
              zoomOnScroll
            >
              <Background gap={24} size={1} />
              <MiniMap pannable zoomable />
              <Controls showInteractive={false} />
            </ReactFlow>
          ) : (
            <p className="p-3 text-sm text-fg-muted">Loading DAG…</p>
          )}
        </div>

        {/*
          K4.3 (round-5) — lifecycle detail drawer. Same aside as before,
          but now with (a) a "Jump to artifacts" link so a PM/PO can go
          straight from a clicked node to its outputs, and (b) a
          close-drawer button so keyboard users can dismiss the detail
          view without having to click the empty canvas.
        */}
        <aside aria-label="Node detail" className="shakti-card">
          <div className="flex items-start justify-between gap-2">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-fg-muted">
              Node detail
            </h2>
            {selectedNode ? (
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="text-2xs uppercase tracking-wider text-fg-muted hover:text-fg-primary"
                aria-label="Close node detail"
              >
                ✕
              </button>
            ) : null}
          </div>
          {selectedNode ? (
            <div className="mt-2 flex flex-col gap-2 text-sm">
              <div>
                <div className="text-xs text-fg-muted">ID</div>
                <div className="shakti-mono">{selectedNode.id}</div>
              </div>
              <div>
                <div className="text-xs text-fg-muted">Label</div>
                <div>{selectedNode.label}</div>
              </div>
              <div>
                <div className="text-xs text-fg-muted">Status</div>
                <div>
                  <span className={statusChip(selectedNode.status)}>{selectedNode.status}</span>
                </div>
              </div>
              <div>
                <div className="text-xs text-fg-muted">Incoming</div>
                <div className="shakti-mono text-2xs">
                  {incoming.map((e) => e.source).join(', ') || '—'}
                </div>
              </div>
              <div>
                <div className="text-xs text-fg-muted">Outgoing</div>
                <div className="shakti-mono text-2xs">
                  {outgoing.map((e) => e.target).join(', ') || '—'}
                </div>
              </div>
              <Link
                to="/dashboard/artifacts"
                className="mt-2 inline-flex items-center justify-center rounded-sm border border-accent/40 bg-accent-muted px-3 py-1.5 text-xs font-medium text-accent"
              >
                View artifacts →
              </Link>
            </div>
          ) : (
            <p className="mt-2 text-xs text-fg-muted">Select a phase to inspect.</p>
          )}
        </aside>
      </div>
    </section>
  );
}

// ── Layout + transform helpers ────────────────────────────────────────────

type PhaseNodeData = { label: string; status: string };

function toReactFlow(
  dag: { nodes: Array<{ id: string; label: string; status: string }>; edges: Array<{ source: string; target: string }> } | null,
): { nodes: Node<PhaseNodeData>[]; edges: Edge[] } {
  if (!dag) return { nodes: [], edges: [] };

  // Compute depth (topological level) per node via BFS from roots.
  const incoming = new Map<string, string[]>();
  dag.edges.forEach((e) => {
    const list = incoming.get(e.target) ?? [];
    list.push(e.source);
    incoming.set(e.target, list);
  });

  const depth = new Map<string, number>();
  const visit = (id: string, d: number) => {
    const prev = depth.get(id) ?? -1;
    if (d <= prev) return;
    depth.set(id, d);
    dag.edges
      .filter((e) => e.source === id)
      .forEach((e) => visit(e.target, d + 1));
  };
  dag.nodes.forEach((n) => {
    if (!incoming.has(n.id)) visit(n.id, 0);
  });
  dag.nodes.forEach((n) => {
    if (!depth.has(n.id)) depth.set(n.id, 0);
  });

  // Group by depth, assign y per level.
  const levels = new Map<number, string[]>();
  dag.nodes.forEach((n) => {
    const d = depth.get(n.id) ?? 0;
    const list = levels.get(d) ?? [];
    list.push(n.id);
    levels.set(d, list);
  });

  const xSpacing = 220;
  const ySpacing = 120;
  const nodes: Node<PhaseNodeData>[] = dag.nodes.map((n) => {
    const d = depth.get(n.id) ?? 0;
    const siblings = levels.get(d) ?? [];
    const i = siblings.indexOf(n.id);
    const cols = siblings.length;
    const offsetY = (i - (cols - 1) / 2) * ySpacing;
    return {
      id: n.id,
      type: 'phase',
      position: { x: d * xSpacing, y: offsetY },
      data: { label: n.label, status: n.status },
    };
  });

  const edges: Edge[] = dag.edges.map((e, i) => ({
    id: `e-${i}-${e.source}-${e.target}`,
    source: e.source,
    target: e.target,
    animated: false,
    markerEnd: { type: MarkerType.ArrowClosed },
  }));

  return { nodes, edges };
}

function PhaseNode({ data }: NodeProps<Node<PhaseNodeData>>): JSX.Element {
  return (
    <div className={`rounded-sm border px-3 py-2 shadow-sm ${phaseNodeClass(data.status)}`}
         style={{ minWidth: 140, background: '#0b1012' }}>
      <Handle type="target" position={Position.Left} />
      <div className="shakti-mono text-xs text-fg-primary">{data.label}</div>
      <div className="mt-1 text-2xs uppercase tracking-wider text-fg-muted">
        {data.status}
      </div>
      <Handle type="source" position={Position.Right} />
    </div>
  );
}

function phaseNodeClass(status: string): string {
  switch (status) {
    case 'completed':
      return 'border-status-success';
    case 'running':
      return 'border-accent animate-pulse motion-reduce:animate-none';
    case 'failed':
      return 'border-status-error';
    case 'pending':
    default:
      return 'border-border-subtle';
  }
}

function statusChip(status: string): string {
  const base = 'inline-block rounded-sm px-2 py-0.5 text-2xs uppercase tracking-wider ';
  switch (status) {
    case 'completed':
      return base + 'bg-status-success/15 text-status-success';
    case 'running':
      return base + 'bg-accent/15 text-accent';
    case 'failed':
      return base + 'bg-status-error/15 text-status-error';
    default:
      return base + 'bg-bg-raised text-fg-muted';
  }
}
